// @/src/core/utils/helpers.ts

import { Timestamp, FieldValue } from 'firebase/firestore';

/**
 * @file src/core/utils/helpers.ts
 * @description Caja de herramientas con funciones de utilidad puras y reutilizables.
 */

// --- Funciones de Logging (Solo en Desarrollo) ---
/**
 * Función de logging que solo muestra mensajes en modo desarrollo.
 * En producción, estos logs se eliminan automáticamente por el bundler.
 * @param args - Argumentos a loguear (igual que console.log)
 */
export const devLog = (...args: unknown[]): void => {
  if (import.meta.env.MODE === 'development') {
    console.log(...args);
  }
};

// --- Control de flujo ---
export function debounce<T extends (...args: never[]) => void>(fn: T, ms: number = 250): (...args: Parameters<T>) => void {
    let timer: ReturnType<typeof setTimeout>;
    return (...args: Parameters<T>) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), ms);
    };
}

// --- Funciones de DOM y Selectores ---
export const getEl = <T extends HTMLElement>(selector: string, parent: Document | HTMLElement = document): T | null => parent.querySelector<T>(selector);
export const getAllEl = (selector: string, parent: Document | HTMLElement = document): NodeListOf<HTMLElement> => parent.querySelectorAll<HTMLElement>(selector);

// --- Generación de IDs y Fechas ---
export const generateId = (prefix: string = 'id'): string => `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).substring(2, 9)}`;
export const getCurrentDateForInputGT = (date: Date | Timestamp | null = null): string => {
    const d = date ? (date instanceof Timestamp ? date.toDate() : date) : new Date();
    const gtDate = new Date(d.toLocaleString("en-US", { timeZone: "America/Guatemala" }));
    return `${gtDate.getFullYear()}-${String(gtDate.getMonth() + 1).padStart(2, '0')}-${String(gtDate.getDate()).padStart(2, '0')}`;
};

// --- Formateo de Datos ---
// escapeHtml vive ahora en shared/ (fuente única frontend + functions).
export { escapeHtml } from '@shared/utils/escape-html';
export const formatDateForDisplay = (dateValue: string | Timestamp | Date | FieldValue | null | undefined): string => {
    if (!dateValue) return '';

    let date: Date;
    if (dateValue instanceof Timestamp) {
        date = dateValue.toDate();
    } else if (dateValue instanceof Date) {
        date = dateValue;
    } else if (typeof dateValue === 'string') {
        if (dateValue.includes('-')) {
            const parts = dateValue.split('-');
            if (parts.length === 3 && parts[0].length === 4) return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        date = new Date(dateValue);
    } else {
        // Se asume que es un FieldValue si es un objeto pero no Timestamp o Date
        return 'justo ahora';
    }
    
    if (date && !isNaN(date.getTime())) {
        return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
    }
    return String(dateValue);
};
export const formatCurrency = (amount: number | null | undefined, simbolo: string = 'Q'): string => {
    if (typeof amount !== 'number' || isNaN(amount)) return `${simbolo}0.00`;
    return `${simbolo}${amount.toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
export const formatNumberWithCommas = (num: number | string | null | undefined): string => {
    if (num === null || num === undefined || num === '') return '0';
    const n = typeof num === 'number' ? num : Number(num);
    if (isNaN(n)) return '0';
    return n.toLocaleString('en-US');
};
export const formatTelefono = (telefono: string | null | undefined): string => {
    if (!telefono || typeof telefono !== 'string') return '';
    // Remover cualquier guión existente y caracteres no numéricos
    const numeros = telefono.replace(/\D/g, '');
    
    // Si tiene 11 dígitos y empieza con 502 (formato guatemalteco completo)
    if (numeros.length === 11 && numeros.startsWith('502')) {
        const principal = numeros.substring(3);
        return `+502 ${principal.substring(0, 4)}-${principal.substring(4)}`;
    }
    
    // Si tiene 8 dígitos (formato guatemalteco simple)
    if (numeros.length === 8) {
        return `${numeros.substring(0, 4)}-${numeros.substring(4)}`;
    }
    
    // Si ya tiene el formato correcto internacional
    if (/^\+502\s\d{4}-\d{4}$/.test(telefono)) {
        return telefono;
    }
    
    // Si ya tiene el formato correcto simple, devolverlo tal cual
    if (/^\d{4}-\d{4}$/.test(telefono)) {
        return telefono;
    }

    // Si tiene el prefijo pero no está bien estructurado
    if (telefono.startsWith('502') && telefono.length > 3) {
        const principal = telefono.substring(3).replace(/\D/g, '');
        if (principal.length === 8) {
            return `+502 ${principal.substring(0, 4)}-${principal.substring(4)}`;
        }
    }
    
    // Si no tiene 8 o 11 dígitos, devolver tal cual (puede estar incompleto)
    return telefono;
};

// --- UI Helpers ---
export const getStatusSelectClass = (status: string): string => {
    const statusClasses: { [key: string]: string } = {
        "Procesado": "procesado", "Recolectado": "recolectado", "1# En Camino": "recolectado1",
        "2# En Camino": "recolectado2", "3# En Camino": "recolectado3", "Entregado": "entregado",
        "Liquidado": "liquidado", "Devolución": "devolucion", "Devolución Entregada": "devolucionentregada",
        "Pérdida": "perdida", "Cancelado": "cancelado",
        // Agencias - Mismo color naranja que En Camino
        "1# En Agencia": "recolectado1", "2# En Agencia": "recolectado1", 
        "3# En Agencia": "recolectado1", "4# En Agencia": "recolectado1", "5# En Agencia": "recolectado1"
    };
    return `status-select ${statusClasses[status] || ''}`;
};
export const copyToClipboard = async (text: string, iconElement: HTMLElement): Promise<boolean> => {
    try {
        await navigator.clipboard.writeText(text);
        const originalClass = iconElement.className;
        iconElement.className = 'fas fa-check text-green-500';
        setTimeout(() => { iconElement.className = originalClass; }, 1500);
        return true;
    } catch (err) {
        console.error('Error al copiar:', err);
        return false;
    }
};
export const highlightText = (text: string, searchTerm: string): string => {
    if (!searchTerm || typeof text !== 'string') return text;
    const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return text.replace(new RegExp(`(${escaped})`, 'gi'), `<span class="search-highlight">$1</span>`);
};
export const sanitizeSearchTerm = (term: string | null | undefined): string => {
    if (typeof term !== 'string') return '';
    return term.replace(/[\u0000-\u001F\u007F-\u009F\u200B-\u200F\uFEFF]/g, "").trim().toLowerCase();
};

/**
 * Convierte una fecha YYYY-MM-DD a DD/MM/YYYY.
 */
export const formatDateToDDMMYYYY = (dateStr: string): string => {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
};

export const formatDateToInput = (dateValue: string | Timestamp | Date | null | undefined): string => {
    if (!dateValue) return '';
    const date = (dateValue instanceof Timestamp) ? dateValue.toDate() : new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const formatDateTimeForDisplay = (dateValue: string | Timestamp | Date | FieldValue | null | undefined): string => {
    if (!dateValue) return 'N/A';
    
    let date: Date;
    if (dateValue instanceof Timestamp) {
        date = dateValue.toDate();
    } else if (dateValue instanceof Date) {
        date = dateValue;
    } else if (typeof dateValue === 'string' || typeof dateValue === 'number') {
        date = new Date(dateValue);
    } else {
        return 'justo ahora';
    }

    if (date && !isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12;
        const strHours = String(hours).padStart(2, '0');
        return `${day}/${month}/${year} ${strHours}:${minutes} ${ampm}`;
    }
    return String(dateValue);
};


export const getFileIcon = (mimeType: string | null | undefined): string => {
    if (!mimeType) return 'fa-file'; 

    const type = mimeType.toLowerCase();

    if (type.includes('image')) return 'fa-file-image';
    if (type.includes('pdf')) return 'fa-file-pdf';
    if (type.includes('word') || type.includes('vnd.openxmlformats-officedocument.wordprocessingml')) return 'fa-file-word';
    if (type.includes('excel') || type.includes('vnd.openxmlformats-officedocument.spreadsheetml')) return 'fa-file-excel';
    if (type.includes('presentation') || type.includes('vnd.openxmlformats-officedocument.presentationml')) return 'fa-file-powerpoint';
    if (type.includes('audio')) return 'fa-file-audio';
    if (type.includes('video')) return 'fa-file-video';
    if (type.includes('archive') || type.includes('zip') || type.includes('rar')) return 'fa-file-archive';
    if (type.includes('text')) return 'fa-file-alt';

    return 'fa-file';
};

export const getEstadoPedidoBadgeClass = (estado: string): string => {
    switch (estado) {
        case 'Procesado': return 'text-orange-700';
        case 'Recolectado': return 'text-orange-800';
        case '1# En Camino': return 'text-orange-800';
        case '2# En Camino': return 'text-orange-800';
        case '3# En Camino': return 'text-red-700';
        case 'Entregado': return 'text-lime-700';
        case 'Liquidado': return 'text-green-700';
        case 'Devolución': return 'text-red-700';
        case 'Devolución Entregada': return 'text-red-500';
        case 'Cancelado': return 'text-gray-600';
        default: return 'text-gray-600';
    }
};

export const getEstadoClienteBadgeClass = (estado: string): string => {
    switch (estado) {
        case 'Cliente Activo': 
            return 'text-[#ffffff] bg-[#459962]';
        case 'Cliente Potencial': 
            return 'text-[#ffffff] bg-[#f5b102]';
        case 'Cliente Inactivo': 
            return 'text-[#ffffff] bg-[#6b7280]';
        case 'Cliente Perdido': 
            return 'text-[#ffffff] bg-[#e24a4a]';
        case 'Cliente Nuevo': 
            return 'text-[#ffffff] bg-[#3b82f6]';
        default: 
            return 'text-[#ffffff] bg-[#f5b102]';
    }
};
